/**
 * Phase 107 (Plan 107-09 / SLA-PRICE-06, SLA-PRICE-07) — SLA Pricing
 * Configurator list page.
 *
 * Modelled directly on `LeadsPage.tsx`'s header/composition shape. Named
 * configurations render side by side (D-10) with their own currency and
 * server-computed monthly price (D-13, D-14) — no conversion, exchange rate
 * or CZK-equivalent column exists anywhere on this page, and no project or
 * customer field or column exists anywhere (D-11).
 *
 * MKS-346 adds the one lead-shaped thing on this page: a "Leads" count column
 * and the unlink line on the delete confirmation.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { NewConfigurationModal } from '../../components/sla-pricing/NewConfigurationModal';
import { DeleteConfigConfirmModal } from '../../components/sla-pricing/DeleteConfigConfirmModal';
import {
  useConfigurations,
  useCreateConfiguration,
  useDuplicateConfiguration,
  useDeleteConfiguration,
} from '../../hooks/useSlaPricing';
import { formatDisplayDateTime } from '../../utils/format';
import type {
  CreateConfigurationInput,
  SlaPricingConfigurationSummary,
} from '../../services/api/slaPricing';

export default function SlaPricingListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useConfigurations();
  const createConfiguration = useCreateConfiguration();
  const duplicateConfiguration = useDuplicateConfiguration();
  const deleteConfiguration = useDeleteConfiguration();

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SlaPricingConfigurationSummary | null>(null);

  const configurations = data?.data ?? [];

  function handleCreate(input: CreateConfigurationInput) {
    createConfiguration.mutate(input, {
      onSuccess: (result) => {
        setIsNewOpen(false);
        navigate(`/sales/sla-pricing/${result.data.id}`);
      },
    });
  }

  function handleDuplicate(id: number) {
    duplicateConfiguration.mutate(id, {
      onSuccess: (result) => {
        navigate(`/sales/sla-pricing/${result.data.id}`);
      },
    });
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteConfiguration.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  function openEditor(id: number) {
    navigate(`/sales/sla-pricing/${id}`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SLA Pricing Configurator</h1>
        <Button onClick={() => setIsNewOpen(true)}>+ New configuration</Button>
      </div>

      {configurations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12 text-gray-400">
          <p className="mb-1 text-[13px]">No configurations yet.</p>
          <p className="mb-4 text-[13px]">
            Price an SLA package to get a monthly quote — create one to start.
          </p>
          <Button onClick={() => setIsNewOpen(true)}>+ New configuration</Button>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Currency</th>
              <th className="py-2 pr-4">Monthly price</th>
              {/* MKS-346 — which configurations are actually in use. */}
              <th className="py-2 pr-4">Leads</th>
              <th className="py-2 pr-4">Updated</th>
              <th className="py-2 pr-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {configurations.map((config) => (
              <tr key={config.id}>
                <td className="py-3 pr-4 text-[13px] font-medium text-gray-900">{config.name}</td>
                <td className="py-3 pr-4">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                    {config.currency}
                  </span>
                </td>
                <td className="py-3 pr-4 font-mono tabular-nums text-[13px] text-gray-900">
                  {config.monthlyPrice}
                </td>
                <td className="py-3 pr-4 tabular-nums text-[13px] text-gray-500">
                  {config.leadCount > 0 ? config.leadCount : '—'}
                </td>
                <td className="py-3 pr-4 text-[13px] text-gray-500">
                  {formatDisplayDateTime(config.updatedAt)}
                </td>
                <td className="py-3 pr-4">
                  <div className="flex items-center justify-end gap-3">
                    <a
                      href={`/sales/sla-pricing/${config.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        openEditor(config.id);
                      }}
                      className="text-[13px] font-medium text-primary-600 hover:underline"
                    >
                      Open
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDuplicate(config.id)}
                      aria-label={`Duplicate ${config.name}`}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(config)}
                      aria-label={`Delete ${config.name}`}
                      className="text-gray-400 hover:text-rose-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <NewConfigurationModal
        isOpen={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onCreate={handleCreate}
        isSubmitting={createConfiguration.isPending}
      />

      <DeleteConfigConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        configurationName={deleteTarget?.name ?? ''}
        leadCount={deleteTarget?.leadCount ?? 0}
        isSubmitting={deleteConfiguration.isPending}
      />
    </div>
  );
}
